import { useState, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { QUERY_KEYS } from "@/hooks/use-queries";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { format } from "date-fns";
import type { HospitalPartner, HealthcareProvider } from "@shared/schema";
import { Calendar as CalendarIcon, AlertCircle, Check, ChevronsUpDown } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { partnerFormSchema, type PartnerFormValues } from "./shared";

export function PartnerFormDialog({ 
  providers, 
  existingPartners,
  partner,
  open,
  onOpenChange,
  onSuccess 
}: { 
  providers: HealthcareProvider[]; 
  existingPartners: HospitalPartner[];
  partner?: HospitalPartner;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const isEdit = !!partner;
  const [providerComboOpen, setProviderComboOpen] = useState(false);
  
  const form = useForm<PartnerFormValues>({
    mode: 'onBlur',
    resolver: zodResolver(partnerFormSchema),
    defaultValues: partner ? {
      healthcareProviderId: partner.healthcareProviderId || "",
      partnerName: partner.partnerName,
      contactName: partner.contactName || "",
      contactEmail: partner.contactEmail || "",
      contactPhone: partner.contactPhone || "",
      status: partner.status as PartnerFormValues["status"],
      paymentTermsDays: partner.paymentTermsDays ?? 30,
      revenueSharePercent: partner.revenueSharePercent ?? 0,
      monthlyMinimumOrders: partner.monthlyMinimumOrders ?? 0,
      contractStartDate: partner.contractStartDate ? new Date(partner.contractStartDate) : undefined,
      contractEndDate: partner.contractEndDate ? new Date(partner.contractEndDate) : undefined,
      notes: partner.notes || "",
    } : {
      healthcareProviderId: "",
      partnerName: "",
      contactName: "",
      contactEmail: "",
      contactPhone: "",
      status: "pending",
      paymentTermsDays: 30,
      revenueSharePercent: 0,
      monthlyMinimumOrders: 0,
      notes: "",
    },
  });

  useEffect(() => {
    if (open && partner) {
      form.reset({
        healthcareProviderId: partner.healthcareProviderId || "",
        partnerName: partner.partnerName,
        contactName: partner.contactName || "",
        contactEmail: partner.contactEmail || "",
        contactPhone: partner.contactPhone || "",
        status: partner.status as PartnerFormValues["status"],
        paymentTermsDays: partner.paymentTermsDays ?? 30,
        revenueSharePercent: partner.revenueSharePercent ?? 0,
        monthlyMinimumOrders: partner.monthlyMinimumOrders ?? 0,
        contractStartDate: partner.contractStartDate ? new Date(partner.contractStartDate) : undefined,
        contractEndDate: partner.contractEndDate ? new Date(partner.contractEndDate) : undefined,
        notes: partner.notes || "",
      });
    } else if (open && !partner) {
      form.reset({
        healthcareProviderId: "",
        partnerName: "",
        contactName: "",
        contactEmail: "",
        contactPhone: "",
        status: "pending",
        paymentTermsDays: 30,
        revenueSharePercent: 0,
        monthlyMinimumOrders: 0,
        notes: "",
      });
    }
  }, [open, partner]);
  
  const createMutation = useMutation({
    mutationFn: (data: any) => apiRequest("POST", "/api/hospital-partners", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.hospitalPartners });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/hospital-partners"] });
      toast({ title: t('hospitalPartners.partnerCreated', 'Partner Created'), description: t('hospitalPartners.partnerCreatedDesc', 'Hospital partner has been created successfully.') });
      onOpenChange(false);
      onSuccess();
    },
    onError: (error: any) => {
      toast({ title: t('common.error', 'Error'), description: error.message, variant: "destructive" });
    },
  });
  
  const updateMutation = useMutation({
    mutationFn: (data: any) => apiRequest("PATCH", `/api/hospital-partners/${partner?.id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.hospitalPartners });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/hospital-partners"] });
      queryClient.invalidateQueries({ queryKey: ["/api/hospital-partners", partner?.id, "dashboard"] });
      toast({ title: t('hospitalPartners.partnerUpdated', 'Partner Updated'), description: t('hospitalPartners.partnerUpdatedDesc', 'Hospital partner has been updated successfully.') });
      onOpenChange(false);
      onSuccess();
    },
    onError: (error: any) => {
      toast({ title: t('common.error', 'Error'), description: error.message, variant: "destructive" });
    },
  });
  
  const onSubmit = (values: PartnerFormValues) => {
    const submitData = {
      ...values,
      contractStartDate: values.contractStartDate?.toISOString(),
      contractEndDate: values.contractEndDate?.toISOString(),
    };
    
    if (isEdit) {
      updateMutation.mutate(submitData);
    } else {
      createMutation.mutate(submitData);
    }
  };

  const handleProviderSelect = (providerId: string) => {
    form.setValue("healthcareProviderId", providerId, { shouldValidate: true });
    setProviderComboOpen(false);

    const selectedProvider = providers.find(p => p.id === providerId);
    if (selectedProvider && !isEdit) {
      const currentName = form.getValues("partnerName");
      const currentContact = form.getValues("contactName");
      const currentEmail = form.getValues("contactEmail");
      const currentPhone = form.getValues("contactPhone");

      if (!currentName) {
        form.setValue("partnerName", `${selectedProvider.name} Partnership`);
      }
      if (!currentEmail && selectedProvider.email) {
        form.setValue("contactEmail", selectedProvider.email);
      }
      if (!currentPhone && selectedProvider.phone) {
        form.setValue("contactPhone", selectedProvider.phone);
      }
    }
  };
  
  const hospitalProviders = providers.filter(p => p.providerType === "hospital");
  const isPending = createMutation.isPending || updateMutation.isPending;

  const selectedProviderId = form.watch("healthcareProviderId");
  const selectedProvider = hospitalProviders.find(p => p.id === selectedProviderId);

  const duplicatePartner = !isEdit && selectedProviderId
    ? existingPartners.find(
        p => p.healthcareProviderId === selectedProviderId || p.facilityId === selectedProviderId
      )
    : null;
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? t('hospitalPartners.editPartner', 'Edit Hospital Partner') : t('hospitalPartners.createPartner', 'Create Hospital Partner')}
          </DialogTitle>
          <DialogDescription>
            {isEdit ? t('hospitalPartners.editPartnerDesc', 'Update the hospital partner details.') : t('hospitalPartners.createPartnerDesc', 'Add a new hospital partner to the network.')}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
            <div>
              <p className="text-sm font-medium text-muted-foreground mb-3">{t('hospitalPartners.sectionProvider', 'Provider & Identity')}</p>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="healthcareProviderId"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>{t('hospitalPartners.facility', 'Healthcare Provider')}</FormLabel>
                      <Popover open={providerComboOpen} onOpenChange={setProviderComboOpen}>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant="outline"
                              role="combobox"
                              aria-expanded={providerComboOpen}
                              className="w-full justify-between font-normal"
                              data-testid="select-provider"
                            >
                              {selectedProvider
                                ? selectedProvider.name
                                : t('hospitalPartners.selectFacility', 'Search hospitals...')}
                              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-[400px] p-0" align="start">
                          <Command>
                            <CommandInput placeholder={t('hospitalPartners.searchProviders', 'Search by hospital name...')} data-testid="input-search-provider" />
                            <CommandList>
                              <CommandEmpty>{t('hospitalPartners.noProvidersFound', 'No hospitals found.')}</CommandEmpty>
                              <CommandGroup>
                                {hospitalProviders.map((p) => (
                                  <CommandItem
                                    key={p.id}
                                    value={p.name}
                                    onSelect={() => handleProviderSelect(p.id)}
                                    data-testid={`option-provider-${p.id}`}
                                  >
                                    <Check
                                      className={cn(
                                        "mr-2 h-4 w-4",
                                        field.value === p.id ? "opacity-100" : "opacity-0"
                                      )}
                                    />
                                    <div>
                                      <div>{p.name}</div>
                                      {p.city && p.state && (
                                        <div className="text-xs text-muted-foreground">{p.city}, {p.state}</div>
                                      )}
                                    </div>
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                      {duplicatePartner && (
                        <div className="flex items-start gap-2 rounded-md border border-yellow-300 bg-yellow-50 dark:border-yellow-700 dark:bg-yellow-950 p-2 text-xs" data-testid="warning-duplicate-partner">
                          <AlertCircle className="h-4 w-4 text-yellow-600 dark:text-yellow-400 shrink-0 mt-0.5" />
                          <span className="text-yellow-800 dark:text-yellow-300">
                            This provider already has a partnership: <strong>{duplicatePartner.partnerName}</strong> ({duplicatePartner.status}).
                            Creating another may cause conflicts.
                          </span>
                        </div>
                      )}
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="partnerName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('hospitalPartners.partnerName', 'Partner Name')}</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder={t('hospitalPartners.partnerNamePlaceholder', 'e.g., Mayo Clinic Partnership')}
                          data-testid="input-partner-name"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <Separator />

            <div>
              <p className="text-sm font-medium text-muted-foreground mb-3">{t('hospitalPartners.sectionContact', 'Contact Information')}</p>
              <div className="grid grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="contactName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('hospitalPartners.contactName', 'Contact Name')}</FormLabel>
                      <FormControl>
                        <Input {...field} data-testid="input-contact-name" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="contactEmail"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('hospitalPartners.contactEmail', 'Email')}</FormLabel>
                      <FormControl>
                        <Input {...field} type="email" data-testid="input-contact-email" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="contactPhone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('hospitalPartners.contactPhone', 'Phone')}</FormLabel>
                      <FormControl>
                        <Input {...field} data-testid="input-contact-phone" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {isEdit && (
              <>
                <Separator />
                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('hospitalPartners.status', 'Status')}</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger data-testid="select-status">
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="pending">{t('hospitalPartners.statusPending', 'Pending')}</SelectItem>
                          <SelectItem value="active">{t('hospitalPartners.statusActive', 'Active')}</SelectItem>
                          <SelectItem value="suspended">{t('hospitalPartners.statusSuspended', 'Suspended')}</SelectItem>
                          <SelectItem value="inactive">{t('hospitalPartners.statusInactive', 'Inactive')}</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            )}

            <Separator />

            <div>
              <p className="text-sm font-medium text-muted-foreground mb-3">{t('hospitalPartners.sectionContract', 'Contract Terms')}</p>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="contractStartDate"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>{t('hospitalPartners.contractStart', 'Contract Start Date')}</FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant="outline"
                              className="w-full justify-start text-left font-normal"
                              data-testid="datepicker-start"
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {field.value ? format(field.value, "PPP") : <span className="text-muted-foreground">{t('hospitalPartners.selectStartDate', 'Select start date')}</span>}
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={field.value}
                            onSelect={field.onChange}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="contractEndDate"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>{t('hospitalPartners.contractEnd', 'Contract End Date')}</FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant="outline"
                              className="w-full justify-start text-left font-normal"
                              data-testid="datepicker-end"
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {field.value ? format(field.value, "PPP") : <span className="text-muted-foreground">{t('hospitalPartners.selectEndDate', 'Select end date')}</span>}
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={field.value}
                            onSelect={field.onChange}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-3 gap-4 mt-4">
                <FormField
                  control={form.control}
                  name="paymentTermsDays"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('hospitalPartners.paymentTerms', 'Payment Terms')}</FormLabel>
                      <Select value={String(field.value)} onValueChange={(v) => field.onChange(parseInt(v))}>
                        <FormControl>
                          <SelectTrigger data-testid="select-payment-terms">
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="15">NET 15</SelectItem>
                          <SelectItem value="30">NET 30</SelectItem>
                          <SelectItem value="45">NET 45</SelectItem>
                          <SelectItem value="60">NET 60</SelectItem>
                          <SelectItem value="90">NET 90</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="revenueSharePercent"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('hospitalPartners.revenueShare', 'Revenue Share %')}</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          {...field}
                          data-testid="input-revenue-share"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="monthlyMinimumOrders"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('hospitalPartners.minOrders', 'Min Orders/Mo')}</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min="0"
                          {...field}
                          data-testid="input-min-orders"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <Separator />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('common.notes', 'Notes')}</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      rows={3}
                      className="resize-none"
                      data-testid="input-notes"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="button-cancel">
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button type="submit" disabled={isPending} data-testid="button-submit-partner">
                {isPending ? t('common.saving', 'Saving...') : (isEdit ? t('common.update', 'Update') : t('hospitalPartners.createPartner', 'Create Hospital Partner'))}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
